import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useKpis } from '@/features/analytics/hooks'
import { KpiCards } from '@/features/analytics/components/kpi-cards'
import { RevenuePlanChart } from '@/features/analytics/components/revenue-plan-chart'
import { MarginChart } from '@/features/analytics/components/margin-chart'
import { AbsorptionChart } from '@/features/analytics/components/absorption-chart'
import { FloorPlanChart } from '@/features/analytics/components/floor-plan-chart'
import { SatisfactionChart } from '@/features/analytics/components/satisfaction-chart'
import { Button } from '@/components/ui/button'
import { requireAdmin } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/')({
  beforeLoad: requireAdmin,
  validateSearch: (search: Record<string, unknown>): { month?: string } => ({
    month:
      typeof search.month === 'string' && /^\d{4}-\d{2}$/.test(search.month)
        ? search.month
        : undefined,
  }),
  component: DashboardPage,
})

const currentMonthKey = () => new Date().toISOString().slice(0, 7)

function shiftMonth(key: string, delta: number): string {
  const d = new Date(`${key}-01T00:00:00Z`)
  d.setUTCMonth(d.getUTCMonth() + delta)
  return d.toISOString().slice(0, 7)
}

function monthLabel(key: string): string {
  return new Date(`${key}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function DashboardPage() {
  const navigate = useNavigate({ from: '/' })
  const { month: urlMonth } = Route.useSearch()

  // The report's month key: the URL param when set, otherwise the current
  // month (what the backend defaults to).
  const selectedMonth = urlMonth ?? currentMonthKey()
  const atCurrentMonth = selectedMonth >= currentMonthKey()

  const kpis = useKpis(urlMonth)

  const setMonth = (key: string) =>
    navigate({
      search: (prev) => ({ ...prev, month: key === currentMonthKey() ? undefined : key }),
    })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Dealership performance at a glance.</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label="Previous month"
            onClick={() => setMonth(shiftMonth(selectedMonth, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="w-36 text-center text-sm font-medium">
            {monthLabel(kpis.data?.month ?? selectedMonth)}
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Next month"
            disabled={atCurrentMonth}
            onClick={() => setMonth(shiftMonth(selectedMonth, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <KpiCards report={kpis.data} isLoading={kpis.isLoading} />

      <div className="grid gap-4 lg:grid-cols-2">
        <RevenuePlanChart trends={kpis.data?.trends} isLoading={kpis.isLoading} />
        <MarginChart trends={kpis.data?.trends} isLoading={kpis.isLoading} />
        <AbsorptionChart trends={kpis.data?.trends} isLoading={kpis.isLoading} />
        <FloorPlanChart trends={kpis.data?.trends} isLoading={kpis.isLoading} />
        <SatisfactionChart trends={kpis.data?.trends} isLoading={kpis.isLoading} />
      </div>
    </div>
  )
}
